import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Terminal, Search, RefreshCw, Loader2, Trash2, AlertCircle,
  CheckCircle2, AlertTriangle, Mail, CreditCard, Lock, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_CONFIG = {
  auth:    { color: 'bg-blue-500/20 text-blue-400', icon: Lock },
  payment: { color: 'bg-emerald-500/20 text-emerald-400', icon: CreditCard },
  email:   { color: 'bg-purple-500/20 text-purple-400', icon: Mail },
  error:   { color: 'bg-red-500/20 text-red-400', icon: AlertCircle },
  warning: { color: 'bg-amber-500/20 text-amber-400', icon: AlertTriangle },
  info:    { color: 'bg-gray-500/20 text-gray-400', icon: CheckCircle2 },
};

export default function LogViewer() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [levelCounts, setLevelCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeLevel, setActiveLevel] = useState(null);
  const [page, setPage] = useState(0);
  const [expandedLog, setExpandedLog] = useState(null);
  const LIMIT = 50;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * LIMIT, limit: LIMIT };
      if (search) params.search = search;
      if (activeLevel) params.level = activeLevel;
      
      const res = await axios.get(`${API}/admin/logs/`, { params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setLevelCounts(res.data.level_counts);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, activeLevel]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadLogs();
  };

  const clearLogs = async () => {
    if (!window.confirm('Clear all application logs?')) return;
    try {
      await axios.delete(`${API}/admin/logs/clear`);
      toast.success('Logs cleared');
      loadLogs();
    } catch (err) {
      toast.error('Failed to clear logs');
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return ts; }
  };

  const getLevelBadge = (level) => {
    const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.info;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} text-xs flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {level}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search logs..."
              className="bg-[#252540] border-[#2d2d44] text-white pl-10"
            />
          </div>
          <Button type="submit" variant="outline" className="border-[#2d2d44] text-gray-400">
            <Search className="w-4 h-4" />
          </Button>
        </form>
        <div className="flex gap-2">
          <Button onClick={loadLogs} variant="outline" className="border-[#2d2d44] text-gray-400" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={clearLogs} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" size="icon">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Level Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => { setActiveLevel(null); setPage(0); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            !activeLevel ? 'bg-[#6D4AFF] text-white' : 'bg-[#252540] text-gray-400 hover:text-white'
          }`}
        >
          All ({total})
        </button>
        {Object.entries(levelCounts).map(([level, count]) => (
          <button
            key={level}
            onClick={() => { setActiveLevel(level === activeLevel ? null : level); setPage(0); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
              activeLevel === level ? 'bg-[#6D4AFF] text-white' : 'bg-[#252540] text-gray-400 hover:text-white'
            }`}
          >
            {level} {count > 0 && <span className="opacity-60">({count})</span>}
          </button>
        ))}
      </div>

      {/* Log Entries */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#6D4AFF] animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Terminal className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">{search ? 'No logs matching search' : 'No logs recorded yet'}</p>
            </div>
          ) : (
            <div className="divide-y divide-[#2d2d44]/50">
              {logs.map((log, i) => (
                <div
                  key={log.id || i}
                  className="px-4 py-3 hover:bg-[#252540]/50 cursor-pointer transition-colors"
                  onClick={() => setExpandedLog(expandedLog === i ? null : i)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getLevelBadge(log.level)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{log.message}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {log.user_email && (
                          <span className="text-xs text-gray-500">{log.user_email}</span>
                        )}
                        <span className="text-xs text-gray-600">{log.source}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-gray-600">{formatTime(log.timestamp)}</span>
                      {expandedLog === i ? <ChevronUp className="w-3 h-3 text-gray-600" /> : <ChevronDown className="w-3 h-3 text-gray-600" />}
                    </div>
                  </div>
                  {expandedLog === i && log.details && (
                    <pre className="mt-2 p-3 bg-[#0d0d1a] rounded-lg text-xs text-gray-400 font-mono overflow-x-auto">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              variant="outline"
              size="sm"
              className="border-[#2d2d44] text-gray-400"
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * LIMIT >= total}
              variant="outline"
              size="sm"
              className="border-[#2d2d44] text-gray-400"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
